import '@/styles/app.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { showStaleClientToast } from '@/components/layout/UpdatePrompt.tsx';
import { createAuthClient } from '@/lib/auth.ts';
import { installGlobalErrorHandlers, reportClientError } from '@/lib/client-errors.ts';
import { loadConfig } from '@/lib/config.ts';
import { createLiveConnection, queryKeysForTopic, transportForConfig } from '@/lib/live.ts';
import { browserFlushLock, createOutbox, startOutboxSync } from '@/lib/outbox.ts';
import { createDexieOutboxStorage } from '@/lib/outbox-dexie.ts';
import { createQueryClient } from '@/lib/query-client.ts';
import { type AppServices, ServicesContext } from '@/lib/services.ts';
import { applyThemePreference, readThemePreference } from '@/lib/theme.ts';
import { createApiClient, TRPCProvider } from '@/lib/trpc.ts';
import { errorMessage } from '@/lib/utils.ts';
import { routeTree } from './routeTree.gen.ts';

/**
 * Start-up, in order:
 *  1. theme + global error reporting (before anything can fail)
 *  2. /config.json → which stage, which sign-in, which live-sync transport
 *  3. auth, API client, query cache, offline outbox, live sync
 *  4. router (route guards read auth from the router context)
 */

function createAppRouter(services: AppServices) {
  return createRouter({
    routeTree,
    context: { services },
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}

async function boot(root: Root) {
  const config = await loadConfig();
  const auth = await createAuthClient(config);

  const queryClient = createQueryClient({
    onUnauthorized: () => {
      void auth.signOut();
    },
    onStaleClient: showStaleClientToast,
  });

  const trpcClient = createApiClient(() => auth.getRequestHeaders());

  // Other tabs on this device share the outbox; tell them when it changes.
  const channel =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('bnb-outbox');
  const outbox = createOutbox({
    storage: createDexieOutboxStorage(),
    send: (input) => trpcClient.barnLog.add.mutate(input),
    withLock: browserFlushLock,
    onDelivered: () => {
      for (const queryKey of queryKeysForTopic('barn-log')) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    onChange: () => channel?.postMessage('changed'),
  });
  if (channel) channel.onmessage = () => void outbox.refresh();
  startOutboxSync(outbox);

  const live = createLiveConnection(queryClient, transportForConfig(config));

  const services: AppServices = { config, auth, queryClient, trpcClient, outbox, live };
  const router = createAppRouter(services);

  // Sign-in/out anywhere → re-run route guards (signed-out users land on /login).
  auth.subscribe(() => {
    void router.invalidate();
  });

  root.render(
    <StrictMode>
      <ServicesContext value={services}>
        <QueryClientProvider client={queryClient}>
          <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <RouterProvider router={router} />
          </TRPCProvider>
        </QueryClientProvider>
      </ServicesContext>
    </StrictMode>,
  );
}

function BootError({ message }: { message: string }) {
  return (
    <div className="min-h-dvh bg-bg">
      <div aria-hidden="true" className="h-2 bg-brand" />
      <div role="alert" className="mx-auto grid max-w-lg gap-4 px-4 py-16">
        <p className="type-eyebrow">Bare Naked Birdies planner</p>
        <h1 className="type-page m-0">The planner couldn’t start</h1>
        <p className="text-ink-2">{message}</p>
        <div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-10 rounded-full bg-accent px-5 font-semibold text-on-accent hover:bg-accent-hover"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

applyThemePreference(readThemePreference());
installGlobalErrorHandlers();

const container = document.getElementById('root');
if (!container) throw new Error('index.html is missing <div id="root">');
const root = createRoot(container);
boot(root).catch((err: unknown) => {
  reportClientError(err);
  root.render(<BootError message={errorMessage(err)} />);
});
